import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button, Dialog, Input, Label, Switch, Textarea } from "@/components/ui";
import { CoachOption } from "@/containers/Coach/PromptStudio/components/CoachPicker";
import { ScopeToggle } from "@/containers/Coach/PromptStudio/components/ScopeToggle";
import { GENERATE_FAILED } from "@/containers/Coach/PromptStudio/hooks/useBackgroundMutations";
import { BackgroundAssetServices } from "@/services/bedrock/BackgroundAssetServices";
import { GenerateBackgroundsRequest } from "@/services/bedrock/dtos/BackgroundAsset";
import { designKeys } from "@/services/bedrock/queryKeys";
import { LibraryScope, OptionSelect, PREVIEW_RATIO_ITEMS, PreviewRatio } from "./LibraryToolbar";

export const MIN_COUNT = 1;
export const MAX_COUNT = 10;

export interface AdminGenerateDialogProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  /** The toolbar's coach, used when generating into a coach library. */
  coach: CoachOption | null;
  initialScope: LibraryScope;
  /** Prefilled from an asset's prompt ("New global from this prompt"). */
  initialPrompt?: string;
}

export const AdminGenerateDialog: React.FC<AdminGenerateDialogProps> = ({
  open,
  onOpenChange,
  coach,
  initialScope,
  initialPrompt = "",
}) => {
  const queryClient = useQueryClient();
  const [prompt, setPrompt] = React.useState(initialPrompt);
  const [count, setCount] = React.useState("1");
  const [ratio, setRatio] = React.useState<PreviewRatio>("portrait_4_5");
  const [scope, setScope] = React.useState<LibraryScope>(initialScope);
  const [bulk, setBulk] = React.useState(initialScope === "global");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setPrompt(initialPrompt);
    setCount("1");
    setRatio("portrait_4_5");
    setScope(initialScope);
    setBulk(initialScope === "global");
  }, [open, initialPrompt, initialScope]);

  const changeScope = (next: LibraryScope) => {
    setScope(next);
    setBulk(next === "global");
  };

  const countValue = Number(count);
  const countValid = Number.isInteger(countValue) && countValue >= MIN_COUNT && countValue <= MAX_COUNT;
  const needsCoach = scope === "coach" && coach === null;
  const canSubmit = prompt.trim().length > 0 && countValid && !needsCoach && !busy;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    const request: GenerateBackgroundsRequest = {
      instruction: prompt.trim(),
      count: countValue,
      ratio,
      scope,
      visibility: scope === "global" ? "draft" : "active",
      bulk,
      ...(scope === "coach" && coach ? { coachId: coach.id } : {}),
    };
    setBusy(true);
    try {
      const { requests } = await BackgroundAssetServices.generate(request);
      toast.success(`${requests.length} queued`);
      queryClient.invalidateQueries({ queryKey: designKeys.backgrounds.requests({}).slice(0, 1) });
      queryClient.invalidateQueries({ queryKey: designKeys.backgrounds.queue });
      onOpenChange(false);
    } catch {
      toast.error(GENERATE_FAILED);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => onOpenChange(next)} size="md">
      <Dialog.Header>
        <Dialog.Title>Create with AI</Dialog.Title>
      </Dialog.Header>
      <form id="admin-generate-backgrounds" onSubmit={submit}>
        <Dialog.Content className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="admin-generate-prompt">Prompt</Label>
            <Textarea
              id="admin-generate-prompt"
              value={prompt}
              onChange={event => setPrompt(event.target.value)}
              rows={5}
              disabled={busy}
            />
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="admin-generate-count">How many</Label>
              <Input
                id="admin-generate-count"
                type="number"
                min={MIN_COUNT}
                max={MAX_COUNT}
                value={count}
                onChange={event => setCount(event.target.value)}
                aria-invalid={!countValid || undefined}
                className="w-24"
                disabled={busy}
              />
            </div>
            <OptionSelect
              label="Ratio"
              items={PREVIEW_RATIO_ITEMS}
              value={ratio}
              onChange={value => setRatio(value as PreviewRatio)}
            />
            <ScopeToggle scope={scope} onScopeChange={changeScope} />
          </div>
          <label className="flex items-center gap-3 cursor-pointer text-sm text-slate-700">
            <Switch checked={bulk} onCheckedChange={setBulk} aria-label="Low priority (run when the queue is free)" />
            Low priority (run when the queue is free)
          </label>
        </Dialog.Content>
      </form>
      <Dialog.Footer>
        <Button type="submit" form="admin-generate-backgrounds" loading={busy} disabled={!canSubmit}>
          Create
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};
